/**
 * CustomerSegmentListPage.js - 客户细分管理页
 * 支持类似 Shopify 的客户分群功能
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Tag,
  Popconfirm,
  message,
  Typography,
  Tooltip,
  Select,
  Row,
  Col,
  Statistic,
  Divider,
  Empty,
  List,
  Avatar,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SyncOutlined,
  UserOutlined,
  TeamOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { adminCustomerSegmentApi, adminCustomerTagApi } from '../../api/index';
import RuleGroupEditor from './components/RuleGroupEditor';
import { normalizeToTree, pruneTree, stringifyRuleTree } from '../../utils/segmentRules';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// 预定义的规则字段
const CONDITION_FIELDS = [
  { label: '总消费金额', value: 'totalSpent', type: 'number' },
  { label: '订单数量', value: 'orderCount', type: 'number' },
  { label: '距上次订单天数', value: 'daysSinceLastOrder', type: 'number' },
  { label: '邮箱', value: 'email', type: 'string' },
  { label: '名字', value: 'firstName', type: 'string' },
  { label: '姓氏', value: 'lastName', type: 'string' },
  { label: '城市', value: 'city', type: 'string' },
  { label: '省/州', value: 'province', type: 'string' },
  { label: '国家', value: 'country', type: 'string' },
  { label: '客户标签', value: 'tags', type: 'tag' },
];


export default function CustomerSegmentListPage() {
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [customersModalVisible, setCustomersModalVisible] = useState(false);
  const [editingSegment, setEditingSegment] = useState(null);
  const [saving, setSaving] = useState(false);
  const [segmentType, setSegmentType] = useState('dynamic');
  const [ruleTree, setRuleTree] = useState(normalizeToTree(null));
  const [customers, setCustomers] = useState([]);
  const [viewingSegment, setViewingSegment] = useState(null);
  const [tags, setTags] = useState([]);
  const [form] = Form.useForm();

  // 拉取标签列表
  const fetchTags = useCallback(async () => {
    try {
      const data = await adminCustomerTagApi.list();
      setTags(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('加载标签列表失败', err);
    }
  }, []);

  // 拉取细分列表
  const fetchSegments = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminCustomerSegmentApi.list();
      setSegments(Array.isArray(data) ? data : []);
    } catch (err) {
      message.error('加载细分列表失败：' + (err.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSegments();
    fetchTags();
  }, [fetchSegments, fetchTags]);

  // 打开新建 Modal
  const handleCreate = () => {
    setEditingSegment(null);
    setSegmentType('dynamic');
    setRuleTree(normalizeToTree(null));
    form.setFieldsValue({ name: '', description: '', type: 'dynamic' });
    setModalVisible(true);
  };

  // 打开编辑 Modal
  const handleEdit = (segment) => {
    setEditingSegment(segment);
    setSegmentType(segment.type);

    // 解析规则（兼容老扁平格式和新嵌套树格式）
    if (segment.rules) {
      try {
        setRuleTree(normalizeToTree(JSON.parse(segment.rules)));
      } catch (e) {
        setRuleTree(normalizeToTree(null));
      }
    } else {
      setRuleTree(normalizeToTree(null));
    }

    form.setFieldsValue({
      name: segment.name,
      description: segment.description,
      type: segment.type,
    });
    setModalVisible(true);
  };

  // 关闭 Modal
  const handleModalClose = () => {
    setModalVisible(false);
    setEditingSegment(null);
    form.resetFields();
  };

  // 保存细分
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      let rules = null;
      if (segmentType === 'dynamic') {
        const pruned = pruneTree(ruleTree);
        rules = pruned ? JSON.stringify(pruned) : null;
      }

      if (editingSegment) {
        await adminCustomerSegmentApi.update(editingSegment.id, {
          ...values,
          type: segmentType,
          rules,
        });
        message.success('细分已更新');
      } else {
        await adminCustomerSegmentApi.create({
          ...values,
          type: segmentType,
          rules,
        });
        message.success('细分已创建');
      }
      handleModalClose();
      fetchSegments();
    } catch (err) {
      if (err?.errorFields) return;
      message.error('保存失败：' + (err.message || '未知错误'));
    } finally {
      setSaving(false);
    }
  };

  // 删除细分
  const handleDelete = async (id) => {
    try {
      await adminCustomerSegmentApi.delete(id);
      message.success('细分已删除');
      fetchSegments();
    } catch (err) {
      message.error('删除失败：' + (err.message || '未知错误'));
    }
  };

  // 刷新客户数量
  const handleRefresh = async (id) => {
    try {
      await adminCustomerSegmentApi.refresh(id);
      message.success('客户数量已更新');
      fetchSegments();
    } catch (err) {
      message.error('刷新失败：' + (err.message || '未知错误'));
    }
  };

  // 查看客户列表
  const handleViewCustomers = async (segment) => {
    setViewingSegment(segment);
    setCustomersModalVisible(true);
    try {
      const data = await adminCustomerSegmentApi.getCustomers(segment.id);
      setCustomers(Array.isArray(data) ? data : []);
    } catch (err) {
      message.error('加载客户列表失败：' + (err.message || '未知错误'));
    }
  };

  // 导出客户
  const handleExportCustomers = async (segment) => {
    try {
      message.loading('正在导出...');
      const blob = await adminCustomerSegmentApi.exportCustomers(segment.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const filename = `客户细分_${segment.name.replace(/[^a-zA-Z0-9\u4e00-\u9fa5]/g, '_')}.csv`;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
      message.destroy();
      message.success('客户已导出');
    } catch (err) {
      message.destroy();
      message.error('导出失败：' + (err?.response?.data?.message || err.message || '未知错误'));
    }
  };

  const columns = [
    {
      title: '细分名称',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <div>
          <strong>{name}</strong>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>{record.description || '-'}</Text>
        </div>
      ),
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type) => (
        <Tag color={type === 'dynamic' ? 'blue' : 'green'}>
          {type === 'dynamic' ? '动态' : '静态'}
        </Tag>
      ),
    },
    {
      title: '匹配规则',
      key: 'rules',
      width: 250,
      render: (_, record) => {
        if (record.type === 'static') {
          return <Text type="secondary">手动添加客户</Text>;
        }
        try {
          const rules = record.rules ? JSON.parse(record.rules) : null;
          const tree = rules ? normalizeToTree(rules) : null;
          const text = tree
            ? stringifyRuleTree(tree, { fieldLabel: (f) => CONDITION_FIELDS.find((x) => x.value === f)?.label || f })
            : '';
          if (!text) return <Text type="secondary">无规则</Text>;
          return <div style={{ fontSize: 12, maxWidth: 240, whiteSpace: 'normal' }}>{text}</div>;
        } catch (e) {
          return <Text type="secondary">规则解析失败</Text>;
        }
      },
    },
    {
      title: '客户数',
      dataIndex: 'customerCount',
      key: 'customerCount',
      width: 120,
      render: (count, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Tag color={count > 0 ? 'blue' : 'default'} icon={<UserOutlined />}>
            {count || 0}
          </Tag>
          {record.type === 'dynamic' && (
            <Tooltip title="刷新客户数量">
              <SyncOutlined
                spin={loading}
                style={{ cursor: 'pointer', color: '#1890ff' }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRefresh(record.id);
                }}
              />
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (val) => val ? new Date(val).toLocaleString('zh-CN', { hour12: false }) : '-',
    },
    {
      title: '操作',
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small" direction="vertical" style={{ width: '100%' }}>
          <Space size="small">
            <Tooltip title="查看客户">
              <Button
                type="link"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => handleViewCustomers(record)}
              >
                客户
              </Button>
            </Tooltip>
            <Tooltip title="导出客户">
              <Button
                type="link"
                size="small"
                icon={<DownloadOutlined />}
                onClick={() => handleExportCustomers(record)}
              >
                导出
              </Button>
            </Tooltip>
            {record.type === 'dynamic' && (
              <Tooltip title="刷新数量">
                <Button
                  type="link"
                  size="small"
                  icon={<SyncOutlined />}
                  onClick={() => handleRefresh(record.id)}
                />
              </Tooltip>
            )}
          </Space>
          <Space size="small">
            <Tooltip title="编辑">
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
              />
            </Tooltip>
            <Popconfirm
              title="确认删除该细分？"
              onConfirm={() => handleDelete(record.id)}
              okText="删除"
              cancelText="取消"
              okButtonProps={{ danger: true }}
            >
              <Tooltip title="删除">
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          <TeamOutlined style={{ marginRight: 8 }} />
          客户细分管理
        </Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          新建细分
        </Button>
      </div>

      <Card style={{ borderRadius: 8 }} bodyStyle={{ padding: 0 }}>
        <Table
          dataSource={segments}
          columns={columns}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1100 }}
          pagination={false}
          locale={{ emptyText: '暂无细分' }}
        />
      </Card>

      {/* 新建/编辑 Modal */}
      <Modal
        title={editingSegment ? '编辑细分' : '新建细分'}
        open={modalVisible}
        onCancel={handleModalClose}
        onOk={handleSave}
        okText="保存"
        cancelText="取消"
        confirmLoading={saving}
        width={700}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item
            name="name"
            label="细分名称"
            rules={[{ required: true, message: '请输入细分名称' }]}
          >
            <Input placeholder="例：高价值客户、活跃客户" />
          </Form.Item>

          <Form.Item
            name="description"
            label="细分描述"
          >
            <TextArea rows={2} placeholder="描述该细分的特征..." />
          </Form.Item>

          <Form.Item
            name="type"
            label="细分类型"
            initialValue="dynamic"
          >
            <Select
              value={segmentType}
              onChange={(val) => setSegmentType(val)}
            >
              <Option value="dynamic">动态细分 - 根据规则自动匹配客户</Option>
              <Option value="static">静态细分 - 手动添加客户</Option>
            </Select>
          </Form.Item>

          {segmentType === 'dynamic' && (
            <>
              <Divider orientation="left">匹配条件（支持嵌套分组 = 括号优先级）</Divider>
              <RuleGroupEditor
                node={ruleTree}
                depth={0}
                fields={CONDITION_FIELDS}
                tags={tags}
                onChange={setRuleTree}
              />
            </>
          )}

          {segmentType === 'static' && (
            <Form.Item label="提示">
              <Text type="secondary">
                静态细分需要手动添加客户。创建细分后，在客户列表页面可以选择客户添加到该细分。
              </Text>
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* 查看客户 Modal */}
      <Modal
        title={
          <div>
            <EyeOutlined style={{ marginRight: 8 }} />
            {viewingSegment?.name} - 客户列表
          </div>
        }
        open={customersModalVisible}
        onCancel={() => setCustomersModalVisible(false)}
        footer={null}
        width={700}
      >
        <div style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={8}>
              <Statistic
                title="匹配客户数"
                value={customers.length}
                prefix={<UserOutlined />}
                valueStyle={{ color: '#3f8600' }}
              />
            </Col>
            {viewingSegment?.type === 'dynamic' && viewingSegment?.rules && (
              <Col span={16}>
                <Statistic
                  title="匹配规则"
                  valueStyle={{ fontSize: 12 }}
                  value={
                    <div style={{ fontSize: 12, color: '#666' }}>
                      {(() => {
                        try {
                          const tree = normalizeToTree(JSON.parse(viewingSegment.rules));
                          return stringifyRuleTree(tree, {
                            fieldLabel: (f) => CONDITION_FIELDS.find((x) => x.value === f)?.label || f,
                          }) || '无规则';
                        } catch (e) {
                          return '解析失败';
                        }
                      })()}
                    </div>
                  }
                />
              </Col>
            )}
          </Row>
        </div>
        <Divider />
        {customers.length === 0 ? (
          <Empty description="暂无匹配的客户" />
        ) : (
          <List
            itemLayout="horizontal"
            dataSource={customers}
            renderItem={(customer) => (
              <List.Item>
                <List.Item.Meta
                  avatar={<Avatar icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />}
                  title={customer.firstName || customer.lastName ? `${customer.lastName || ''} ${customer.firstName || ''}`.trim() : '匿名用户'}
                  description={
                    <div>
                      <div>{customer.email}</div>
                      <div style={{ fontSize: 12, color: '#999' }}>
                        订单数：{customer.orderCount || 0} | 总消费：${customer.totalSpent?.toFixed(2) || '0.00'}
                      </div>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Modal>
    </div>
  );
}
